import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { toast } from "sonner";
import { 
  Car, 
  ArrowLeft,
  User,
  Phone,
  Mail,
  MapPin,
  Check,
  Loader2,
  AlertCircle,
  Users
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CANADIAN_PROVINCES = [
  "Alberta", "British Columbia", "Manitoba", "New Brunswick", "Newfoundland and Labrador",
  "Northwest Territories", "Nova Scotia", "Nunavut", "Ontario", "Prince Edward Island",
  "Quebec", "Saskatchewan", "Yukon"
];

export const ReferAFriend = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [errors, setErrors] = useState({});
  const lastSubmitTime = useRef(0);
  
  const [formData, setFormData] = useState({
    // Referrer info
    referrer_name: "",
    referrer_phone: "",
    referrer_email: "",
    // Friend info
    friend_name: "",
    friend_phone: "",
    friend_email: "",
    friend_community: "",
    friend_province: "",
    vehicle_interest: "",
    // Consents
    friend_consent: false,
    referrer_contact_consent: false,
    // Honeypot
    website: ""
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Referrer validation
    if (!formData.referrer_name.trim()) {
      newErrors.referrer_name = "Your name is required";
    }
    if (!formData.referrer_phone.trim()) {
      newErrors.referrer_phone = "Your phone number is required";
    }
    if (!formData.referrer_email.trim()) {
      newErrors.referrer_email = "Your email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.referrer_email)) {
      newErrors.referrer_email = "Please enter a valid email address";
    }
    
    // Friend validation
    if (!formData.friend_name.trim()) {
      newErrors.friend_name = "Friend's name is required";
    }
    if (!formData.friend_phone.trim()) {
      newErrors.friend_phone = "Friend's phone number is required";
    }
    if (!formData.friend_community.trim()) {
      newErrors.friend_community = "Friend's community is required";
    }
    if (!formData.friend_province) {
      newErrors.friend_province = "Province is required";
    }
    
    // Optional email validation
    if (formData.friend_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.friend_email)) {
      newErrors.friend_email = "Please enter a valid email address";
    }
    
    // Consent validation
    if (!formData.friend_consent) {
      newErrors.friend_consent = "You must confirm your friend has consented to be contacted";
    }
    if (!formData.referrer_contact_consent) {
      newErrors.referrer_contact_consent = "You must agree to be contacted about this referral";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Rate limiting - prevent submits within 5 seconds
    const now = Date.now();
    if (now - lastSubmitTime.current < 5000) {
      toast.error("Please wait a moment before submitting again");
      return;
    }
    
    if (!validateForm()) {
      toast.error("Please fix the errors below");
      // Scroll to first error
      const firstErrorField = document.querySelector('.field-error');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    
    // Honeypot check
    if (formData.website) {
      // Silently pretend success for bots
      setIsComplete(true);
      return;
    }
    
    setIsSubmitting(true);
    lastSubmitTime.current = now;
    
    try {
      await axios.post(`${API}/referrals`, {
        referrer_name: formData.referrer_name.trim(),
        referrer_phone: formData.referrer_phone.trim(),
        referrer_email: formData.referrer_email.trim(),
        friend_name: formData.friend_name.trim(),
        friend_phone: formData.friend_phone.trim(),
        friend_email: formData.friend_email.trim() || null,
        friend_community: formData.friend_community.trim(),
        friend_province: formData.friend_province,
        vehicle_interest: formData.vehicle_interest.trim() || null,
        friend_consent: formData.friend_consent,
        referrer_contact_consent: formData.referrer_contact_consent
      });
      
      setIsComplete(true);
      toast.success("Thank you! Your referral has been submitted.");
    } catch (error) {
      console.error("Submission error:", error);
      if (error.response?.status === 429) {
        toast.error("Too many submissions. Please try again later.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderSuccess = () => (
    <div className="success-container fade-in" data-testid="referral-success">
      <div className="success-icon">
        <Check strokeWidth={3} />
      </div>
      <h2>Thank You!</h2>
      <p>
        Your referral has been submitted successfully.<br />
        Our team will reach out to your friend about vehicle financing options.
      </p>
      <div style={{ marginTop: '2rem' }}>
        <Link to="/" className="btn btn-primary" style={{ textDecoration: 'none' }}>
          Back to Home
        </Link>
      </div>
    </div>
  );

  return (
    <div className="refer-page">
      <Helmet>
        <title>Refer a Friend | Moto Money Finance</title>
        <meta name="description" content="Know someone looking for vehicle financing? Refer them to Moto Money Finance and help them find the right vehicle and financing options." />
      </Helmet>

      {/* Header */}
      <header className="site-header">
        <div className="header-container">
          <Link to="/" className="logo" data-testid="logo">
            <Car size={32} />
            <span>Moto Money<strong>Finance</strong></span>
          </Link>
          <nav className="nav-links">
            <Link to="/" className="nav-link">
              <ArrowLeft size={18} />
              Back to Home
            </Link>
          </nav>
        </div>
      </header>

      <div className="refer-container">
        <div className="refer-card">
          {isComplete ? renderSuccess() : (
            <>
              <div className="refer-header">
                <div className="refer-icon">
                  <Users size={32} />
                </div>
                <h1>Refer a Friend to Moto Money</h1>
                <p>
                  Know someone looking for a vehicle? Help them connect with our team for financing options.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="refer-form" data-testid="referral-form">
                {/* Your Information */}
                <div className="form-section">
                  <h2>Your Information</h2>
                  
                  <div className="form-group">
                    <label className="form-label">
                      <User size={14} /> Your Name *
                    </label>
                    <input
                      type="text"
                      className={`form-input ${errors.referrer_name ? 'input-error' : ''}`}
                      placeholder="John Smith"
                      value={formData.referrer_name}
                      onChange={(e) => updateField('referrer_name', e.target.value)}
                      data-testid="referrer-name-input"
                    />
                    {errors.referrer_name && (
                      <span className="field-error" role="alert">
                        <AlertCircle size={14} /> {errors.referrer_name}
                      </span>
                    )}
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">
                        <Phone size={14} /> Your Phone *
                      </label>
                      <input
                        type="tel"
                        className={`form-input ${errors.referrer_phone ? 'input-error' : ''}`}
                        placeholder="(204) 555-1234"
                        value={formData.referrer_phone}
                        onChange={(e) => updateField('referrer_phone', e.target.value)}
                        data-testid="referrer-phone-input"
                      />
                      {errors.referrer_phone && (
                        <span className="field-error" role="alert">
                          <AlertCircle size={14} /> {errors.referrer_phone}
                        </span>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <Mail size={14} /> Your Email *
                      </label>
                      <input
                        type="email"
                        className={`form-input ${errors.referrer_email ? 'input-error' : ''}`}
                        placeholder="john@email.com"
                        value={formData.referrer_email}
                        onChange={(e) => updateField('referrer_email', e.target.value)}
                        data-testid="referrer-email-input"
                      />
                      {errors.referrer_email && (
                        <span className="field-error" role="alert">
                          <AlertCircle size={14} /> {errors.referrer_email}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Friend's Information */}
                <div className="form-section">
                  <h2>Friend's Information</h2>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">
                        <User size={14} /> Friend's Name *
                      </label>
                      <input
                        type="text"
                        className={`form-input ${errors.friend_name ? 'input-error' : ''}`}
                        placeholder="Jane Doe"
                        value={formData.friend_name}
                        onChange={(e) => updateField('friend_name', e.target.value)}
                        data-testid="friend-name-input"
                      />
                      {errors.friend_name && (
                        <span className="field-error" role="alert">
                          <AlertCircle size={14} /> {errors.friend_name}
                        </span>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        <Phone size={14} /> Friend's Phone *
                      </label>
                      <input
                        type="tel"
                        className={`form-input ${errors.friend_phone ? 'input-error' : ''}`}
                        placeholder="(204) 555-5678"
                        value={formData.friend_phone}
                        onChange={(e) => updateField('friend_phone', e.target.value)}
                        data-testid="friend-phone-input"
                      />
                      {errors.friend_phone && (
                        <span className="field-error" role="alert">
                          <AlertCircle size={14} /> {errors.friend_phone}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <Mail size={14} /> Friend's Email (Optional)
                    </label>
                    <input
                      type="email"
                      className={`form-input ${errors.friend_email ? 'input-error' : ''}`}
                      placeholder="jane@email.com"
                      value={formData.friend_email}
                      onChange={(e) => updateField('friend_email', e.target.value)}
                      data-testid="friend-email-input"
                    />
                    {errors.friend_email && (
                      <span className="field-error" role="alert">
                        <AlertCircle size={14} /> {errors.friend_email}
                      </span>
                    )}
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">
                        <MapPin size={14} /> Friend's Community *
                      </label>
                      <input
                        type="text"
                        className={`form-input ${errors.friend_community ? 'input-error' : ''}`}
                        placeholder="Winnipeg"
                        value={formData.friend_community}
                        onChange={(e) => updateField('friend_community', e.target.value)}
                        data-testid="friend-community-input"
                      />
                      {errors.friend_community && (
                        <span className="field-error" role="alert">
                          <AlertCircle size={14} /> {errors.friend_community}
                        </span>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Province *</label>
                      <select
                        className={`form-input form-select ${errors.friend_province ? 'input-error' : ''}`}
                        value={formData.friend_province}
                        onChange={(e) => updateField('friend_province', e.target.value)}
                        data-testid="friend-province-select"
                      >
                        <option value="">Select Province</option>
                        {CANADIAN_PROVINCES.map(province => (
                          <option key={province} value={province}>{province}</option>
                        ))}
                      </select>
                      {errors.friend_province && (
                        <span className="field-error" role="alert">
                          <AlertCircle size={14} /> {errors.friend_province}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <Car size={14} /> Vehicle Interest (Optional)
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g., SUV, Truck, Sedan, specific make/model"
                      value={formData.vehicle_interest}
                      onChange={(e) => updateField('vehicle_interest', e.target.value)}
                      data-testid="vehicle-interest-input"
                    />
                  </div>
                </div>

                {/* Consent Section */}
                <div className="form-section consent-section">
                  <div className={`consent-item ${errors.friend_consent ? 'consent-error' : ''}`}>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.friend_consent}
                        onChange={(e) => updateField('friend_consent', e.target.checked)}
                        data-testid="friend-consent-checkbox"
                      />
                      <span className="checkmark"></span>
                      <span className="consent-text">
                        I confirm that my friend has given me permission to share their contact details 
                        and has consented to being contacted by Moto Money Finance about vehicle financing enquiries. *
                      </span>
                    </label>
                    {errors.friend_consent && (
                      <span className="field-error" role="alert">
                        <AlertCircle size={14} /> {errors.friend_consent}
                      </span>
                    )}
                  </div>

                  <div className={`consent-item ${errors.referrer_contact_consent ? 'consent-error' : ''}`}>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.referrer_contact_consent}
                        onChange={(e) => updateField('referrer_contact_consent', e.target.checked)}
                        data-testid="referrer-consent-checkbox"
                      />
                      <span className="checkmark"></span>
                      <span className="consent-text">
                        I agree to be contacted by Moto Money Finance regarding this referral. *
                      </span>
                    </label>
                    {errors.referrer_contact_consent && (
                      <span className="field-error" role="alert">
                        <AlertCircle size={14} /> {errors.referrer_contact_consent}
                      </span>
                    )}
                  </div>

                  <p className="privacy-note">
                    By submitting this form, you agree to our{" "}
                    <Link to="/privacy">Privacy Policy</Link>.
                  </p>
                </div>

                {/* Honeypot */}
                <div className="hp-field" aria-hidden="true">
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website}
                    onChange={(e) => updateField('website', e.target.value)}
                  />
                </div>

                <button 
                  type="submit" 
                  className="btn btn-primary submit-btn"
                  disabled={isSubmitting}
                  data-testid="submit-referral-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Referral
                      <Check size={18} />
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="site-footer" style={{ marginTop: '0' }}>
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} Moto Money Finance. All rights reserved.</p>
          <p>
            <Link to="/privacy">Privacy Policy</Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ReferAFriend;
